import React from "react";
import MediaCarousel, {
  buildProductMediaUrls,
  isVideoMediaUrl,
} from "@/components/MediaCarousel.jsx";

export function previewFromLookupData(data) {
  const p = extractProductLike(data);
  const imageUrl = firstImageUrl(p);
  const additionalImages = Array.isArray(p.additional_images) ? p.additional_images : [];
  const mediaUrls =
    additionalImages.length > 0 ? buildProductMediaUrls(imageUrl, additionalImages) : [];
  const brand = String(p.title || "").trim();
  const product = String(p.subtitle || "").trim();
  const headline = [brand, product].filter(Boolean).join(" – ") || product || brand || "Product";
  const vol = String(p.volume_label || p.volume || p.size || "").trim();
  const desc = String(p.description || p.details || "").trim();
  return { imageUrl, mediaUrls, title: headline, vol, desc };
}

export function previewFromDraft(draft) {
  const brand = String(draft.title || "").trim();
  const product = String(draft.subtitle || "").trim();
  const headline =
    [brand, product].filter(Boolean).join(" – ") || product || brand || "Listing preview";
  const vol = String(draft.volume_label || "").trim();
  const img = String(draft.image_large || draft.image_small || draft.image || "").trim();
  const desc = String(draft.description || "").trim();
  const additionalImages = Array.isArray(draft.additional_images) ? draft.additional_images : [];
  const mediaUrls =
    additionalImages.length > 0 ? buildProductMediaUrls(img, additionalImages) : [];
  return { imageUrl: img, mediaUrls, title: headline, vol, desc };
}

function extractProductLike(data) {
  if (!data || typeof data !== "object") return {};
  if (data.product && typeof data.product === "object" && !Array.isArray(data.product)) {
    return data.product;
  }
  if (data.data && typeof data.data === "object" && !Array.isArray(data.data)) {
    return data.data;
  }
  if (data.item && typeof data.item === "object") return data.item;
  if (data.result && typeof data.result === "object") return data.result;
  const rest = { ...data };
  delete rest.success;
  delete rest.message;
  delete rest._debug;
  delete rest.query;
  return rest;
}

function firstImageUrl(p) {
  const raw =
    p.image_large ||
    p.image_small ||
    p.image ||
    (Array.isArray(p.images) && p.images[0]) ||
    "";
  if (typeof raw === "string") return raw;
  if (raw && typeof raw === "object" && raw.url) return String(raw.url);
  return "";
}

export function MockListingPreview({ preview, imageLoading = false }) {
  const displaySize = preview.vol || "Each";
  const carouselUrls = Array.isArray(preview.mediaUrls) ? preview.mediaUrls : [];
  const showCarousel = carouselUrls.length > 1;

  return (
    <div className="rounded-lg border border-slate-200 bg-white text-slate-900 overflow-hidden">
      <div className="flex flex-col md:flex-row gap-6 p-5 md:p-8">
        <div className="relative w-full md:w-[280px] shrink-0 mx-auto md:mx-0">
          <div className="aspect-[4/5] rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
            {imageLoading ? (
              <div className="flex flex-col items-center justify-center gap-3 p-6 text-center">
                <span className="inline-flex h-10 w-10 shrink-0 animate-spin rounded-full border-2 border-slate-300 border-t-indigo-600" />
                <span className="text-sm font-medium text-slate-600">Generating catalog image…</span>
              </div>
            ) : showCarousel ? (
              <MediaCarousel urls={carouselUrls} className="h-full" />
            ) : preview.imageUrl || carouselUrls[0] ? (
              isVideoMediaUrl(preview.imageUrl || carouselUrls[0]) ? (
                <video
                  src={preview.imageUrl || carouselUrls[0]}
                  controls
                  playsInline
                  preload="metadata"
                  className="max-h-full max-w-full object-contain"
                />
              ) : (
                <img
                  src={preview.imageUrl || carouselUrls[0]}
                  alt=""
                  className="max-h-full max-w-full object-contain"
                />
              )
            ) : (
              <span className="text-sm text-slate-400">No image</span>
            )}
          </div>
        </div>
        <div className="min-w-0 flex-1 space-y-4">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 leading-tight">{preview.title}</h2>
            <div className="mt-2 flex items-center gap-0.5 text-amber-500" aria-hidden>
              <span className="text-lg leading-none">★★★★</span>
              <span className="text-lg leading-none text-amber-300">★</span>
              <span className="ml-2 text-xs text-slate-500">4.5</span>
            </div>
          </div>

          <div className="rounded-md border border-slate-200 px-4 py-3 flex items-center gap-3">
            <span className="flex h-4 w-4 shrink-0 rounded-full border-2 border-slate-800 bg-slate-800 ring-2 ring-slate-800 ring-offset-2" />
            <span className="text-sm font-medium text-slate-800">{displaySize}</span>
          </div>

          <button
            type="button"
            disabled
            className="w-full rounded-md bg-slate-300 py-3 text-sm font-semibold text-slate-600 cursor-not-allowed"
          >
            Add to cart
          </button>
          <p className="text-xs text-slate-500">Preview only — not a live listing.</p>

          {preview.desc ? (
            <p className="text-sm leading-relaxed text-slate-700 border-t border-slate-100 pt-4">
              {preview.desc}
            </p>
          ) : (
            <p className="text-sm text-slate-400 border-t border-slate-100 pt-4">No description.</p>
          )}
        </div>
      </div>
    </div>
  );
}
